import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useApolloClient } from '@apollo/client/react';
import { gql } from '@apollo/client';
import toast from 'react-hot-toast';
import { GET_MY_DOCUMENTS, GET_MY_CASES } from '../graphql/clientQueries';
import { GET_DOCUMENT_UPLOAD_URL, CREATE_DOCUMENT } from '../graphql/clientMutations';
import { FileViewerModal, FileItem } from '../../components/FileViewerModal';
import {
  validateFileForUpload,
  getFileValidationErrorMessage,
  getFileInputAcceptAttribute,
} from '../../utils/file-validation';
import type { Document, Case } from '../types';

const GET_DOCUMENT_DOWNLOAD_URL = gql`
  query GetDocumentDownloadUrl($id: ID!) {
    getDocumentDownloadUrl(id: $id)
  }
`;

interface GetDocumentDownloadUrlResponse {
  getDocumentDownloadUrl: string;
}

interface GetMyDocumentsResponse {
  listDocumentsByCase: Document[];
}

interface GetDocumentUploadUrlResponse {
  getDocumentUploadUrl: { uploadUrl: string; s3Key: string };
}

interface CreateDocumentResponse {
  createDocument: Document;
}

interface GetMyCasesResponse {
  listMyCases: Case[];
}

type SortOption = 'newest' | 'oldest' | 'name-asc' | 'name-desc' | 'size-desc' | 'size-asc';

interface CaseOption {
  id: string;
  caseNumber: string;
  caseType: string;
  isInquiry: boolean; // true if status is INQUIRY (placeholder case)
}

const ITEMS_PER_PAGE = 10;

const getFileIcon = (fileType: string): string => {
  if (!fileType) return '📎';
  if (fileType.startsWith('image/')) return '🖼️';
  if (fileType.includes('pdf')) return '📄';
  if (fileType.includes('word') || fileType.includes('document')) return '📝';
  if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '📊';
  return '📎';
};

const getFileTypeLabel = (fileType: string): string => {
  if (!fileType) return 'File';
  if (fileType.startsWith('image/')) return 'Image';
  if (fileType.includes('pdf')) return 'PDF';
  if (fileType.includes('word') || fileType.includes('document')) return 'Document';
  if (fileType.includes('excel') || fileType.includes('spreadsheet')) return 'Spreadsheet';
  return 'File';
};

export function DocumentsScreen() {
  const apolloClient = useApolloClient();
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [viewerVisible, setViewerVisible] = useState(false);
  const [viewerFiles, setViewerFiles] = useState<FileItem[]>([]);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [sortOption, setSortOption] = useState<SortOption>('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [filterType, setFilterType] = useState<string>('all');
  const [thumbnailUrls, setThumbnailUrls] = useState<Map<string, string>>(new Map());
  const [caseDropdownOpen, setCaseDropdownOpen] = useState(false);
  const loadedThumbnailsRef = useRef<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const caseDropdownRef = useRef<HTMLDivElement>(null);

  // Fetch ALL cases (including placeholder cases with INQUIRY status)
  const { data: casesData, loading: casesLoading } = useQuery<GetMyCasesResponse>(GET_MY_CASES, {
    fetchPolicy: 'cache-and-network',
  });

  // Build case options - include ALL cases, mark inquiry placeholders
  const caseOptions: CaseOption[] = useMemo(() => {
    const cases = casesData?.listMyCases ?? [];
    return cases.map(c => ({
      id: c.id,
      caseNumber: c.caseNumber,
      caseType: c.caseType || 'Personal Injury',
      isInquiry: c.status === 'INQUIRY',
    }));
  }, [casesData]);

  // Auto-select first case if only one exists
  const effectiveCaseId = selectedCaseId || (caseOptions.length === 1 ? caseOptions[0]?.id : null);
  const selectedCase = caseOptions.find(c => c.id === effectiveCaseId);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (caseDropdownRef.current && !caseDropdownRef.current.contains(event.target as Node)) {
        setCaseDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { data, loading, error, refetch } = useQuery<GetMyDocumentsResponse>(GET_MY_DOCUMENTS, {
    variables: { caseId: effectiveCaseId },
    fetchPolicy: 'cache-and-network',
    skip: !effectiveCaseId,
  });

  const [getUploadUrl] = useMutation<GetDocumentUploadUrlResponse>(GET_DOCUMENT_UPLOAD_URL);
  const [createDocument] = useMutation<CreateDocumentResponse>(CREATE_DOCUMENT);

  const documents = data?.listDocumentsByCase ?? [];

  // Get unique file types for filter
  const fileTypes = useMemo(() => {
    const types = new Set<string>();
    documents.forEach(doc => types.add(getFileTypeLabel(doc.fileType)));
    return Array.from(types).sort();
  }, [documents]);

  // Filter and sort documents
  const filteredAndSortedDocs = useMemo(() => {
    let filtered = [...documents];
    if (filterType !== 'all') {
      filtered = filtered.filter(doc => getFileTypeLabel(doc.fileType) === filterType);
    }
    filtered.sort((a, b) => {
      switch (sortOption) {
        case 'newest': return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'oldest': return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case 'name-asc': return a.fileName.localeCompare(b.fileName);
        case 'name-desc': return b.fileName.localeCompare(a.fileName);
        case 'size-desc': return b.fileSize - a.fileSize;
        case 'size-asc': return a.fileSize - b.fileSize;
        default: return 0;
      }
    });
    return filtered;
  }, [documents, filterType, sortOption]);

  const totalPages = Math.ceil(filteredAndSortedDocs.length / ITEMS_PER_PAGE);
  const paginatedDocs = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredAndSortedDocs.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredAndSortedDocs, currentPage]);

  // Load thumbnails for image documents
  useEffect(() => {
    const loadThumbnails = async () => {
      const imageDocs = paginatedDocs.filter(
        doc => doc.fileType?.startsWith('image/') && !loadedThumbnailsRef.current.has(doc.id)
      );
      for (const doc of imageDocs) {
        loadedThumbnailsRef.current.add(doc.id);
        try {
          const result = await apolloClient.query<GetDocumentDownloadUrlResponse>({
            query: GET_DOCUMENT_DOWNLOAD_URL,
            variables: { id: doc.id },
            fetchPolicy: 'network-only',
          });
          const url = result?.data?.getDocumentDownloadUrl;
          if (url) setThumbnailUrls(prev => new Map(prev).set(doc.id, url));
        } catch { /* ignore */ }
      }
    };
    loadThumbnails();
  }, [paginatedDocs, apolloClient]);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try { await refetch(); } finally { setIsRefreshing(false); }
  }, [refetch]);

  const handlePreview = async (document: Document) => {
    try {
      const result = await apolloClient.query<GetDocumentDownloadUrlResponse>({
        query: GET_DOCUMENT_DOWNLOAD_URL,
        variables: { id: document.id },
        fetchPolicy: 'network-only',
      });
      const url = result?.data?.getDocumentDownloadUrl;
      if (url) {
        setViewerFiles([{ url, fileName: document.fileName, fileType: document.fileType }]);
        setViewerIndex(0);
        setViewerVisible(true);
      } else {
        toast.error('Unable to load document preview');
      }
    } catch (err) {
      console.error('Error getting download URL:', err);
      toast.error('Failed to load document');
    }
  };

  const handleDownload = async (document: Document) => {
    try {
      const result = await apolloClient.query<GetDocumentDownloadUrlResponse>({
        query: GET_DOCUMENT_DOWNLOAD_URL,
        variables: { id: document.id },
        fetchPolicy: 'network-only',
      });
      const url = result?.data?.getDocumentDownloadUrl;
      if (url) window.open(url, '_blank');
      else toast.error('Unable to get download link');
    } catch (err) {
      console.error('Error getting download URL:', err);
      toast.error('Failed to download document');
    }
  };

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';

    const validationResult = validateFileForUpload(file.name, file.type, file.size);
    if (!validationResult.isValid) {
      toast.error(getFileValidationErrorMessage(validationResult) || 'Invalid file');
      return;
    }

    if (!effectiveCaseId) {
      toast.error('Please select a case to upload the document to');
      return;
    }

    setIsUploading(true);
    try {
      const { data: uploadUrlData } = await getUploadUrl({
        variables: { input: { caseId: effectiveCaseId, fileName: file.name, fileType: file.type, fileSize: file.size } },
      });
      if (!uploadUrlData?.getDocumentUploadUrl?.uploadUrl) throw new Error('Failed to get upload URL');

      const { uploadUrl, s3Key } = uploadUrlData.getDocumentUploadUrl;
      const uploadResponse = await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      if (!uploadResponse.ok) throw new Error('Failed to upload file to storage');

      await createDocument({ variables: { input: { caseId: effectiveCaseId, fileName: file.name, fileType: file.type, fileSize: file.size, s3Key } } });
      toast.success('Document uploaded successfully');
      await refetch();
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to upload document');
    } finally {
      setIsUploading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const isDataLoading = (loading || casesLoading) && !data;

  if (isDataLoading) {
    return (
      <div className="bg-white sm:rounded-xl shadow-sm sm:border border-gray-200 h-[calc(100vh-8rem)] sm:h-[calc(100vh-10rem)] min-h-[300px] flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-aalaw-red border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-gray-500">Loading documents...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white sm:rounded-xl shadow-sm sm:border border-gray-200 h-[calc(100vh-8rem)] sm:h-[calc(100vh-10rem)] min-h-[300px] flex items-center justify-center">
        <div className="text-center p-6">
          <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="text-gray-700 font-medium mb-2">Unable to load documents</p>
          <button onClick={() => refetch()} className="text-aalaw-red hover:underline text-sm font-medium">Try again</button>
        </div>
      </div>
    );
  }

  if (caseOptions.length === 0) {
    return (
      <div className="bg-white sm:rounded-xl shadow-sm sm:border border-gray-200 h-[calc(100vh-8rem)] sm:h-[calc(100vh-10rem)] min-h-[300px] flex items-center justify-center">
        <div className="text-center p-6 max-w-sm">
          <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-gray-800 font-semibold mb-2">No Cases Yet</h3>
          <p className="text-gray-500 text-sm">Documents will appear here once you have an active case or inquiry.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white sm:rounded-xl shadow-sm sm:border border-gray-200 h-[calc(100vh-8rem)] sm:h-[calc(100vh-10rem)] min-h-[300px] flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Documents</h1>
            <p className="text-sm text-gray-500">{filteredAndSortedDocs.length} document{filteredAndSortedDocs.length !== 1 ? 's' : ''}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleRefresh} disabled={isRefreshing} className="p-2.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors" title="Refresh">
              <svg className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <button onClick={handleUploadClick} disabled={isUploading || !effectiveCaseId} className="flex items-center gap-2 px-4 py-2.5 bg-aalaw-red hover:bg-aalaw-red-dark disabled:opacity-50 text-white text-sm font-medium rounded-xl transition-colors shadow-sm">
              {isUploading ? (
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
              )}
              <span>Upload</span>
            </button>
            <input ref={fileInputRef} type="file" accept={getFileInputAcceptAttribute()} onChange={handleFileSelect} className="hidden" />
          </div>
        </div>

        {/* Filter Controls */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Case Selector - Custom Dropdown */}
          <div className="relative flex-1 sm:flex-none sm:min-w-[280px]" ref={caseDropdownRef}>
            <button
              onClick={() => setCaseDropdownOpen(!caseDropdownOpen)}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl transition-colors text-left"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${selectedCase?.isInquiry ? 'bg-amber-100' : 'bg-blue-100'}`}>
                  {selectedCase?.isInquiry ? (
                    <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {selectedCase ? (selectedCase.isInquiry ? `Inquiry #${selectedCase.caseNumber}` : `Case #${selectedCase.caseNumber}`) : 'Select a Case'}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {selectedCase ? selectedCase.caseType : 'Choose where to view documents'}
                  </p>
                </div>
              </div>
              <svg className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${caseDropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Dropdown Menu */}
            {caseDropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg z-20 overflow-hidden">
                <div className="max-h-64 overflow-y-auto">
                  {caseOptions.map(caseOpt => (
                    <button
                      key={caseOpt.id}
                      onClick={() => { setSelectedCaseId(caseOpt.id); setCaseDropdownOpen(false); setCurrentPage(1); }}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors ${effectiveCaseId === caseOpt.id ? 'bg-gray-50' : ''}`}
                    >
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${caseOpt.isInquiry ? 'bg-amber-100' : 'bg-blue-100'}`}>
                        {caseOpt.isInquiry ? (
                          <svg className="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {caseOpt.isInquiry ? `Inquiry #${caseOpt.caseNumber}` : `Case #${caseOpt.caseNumber}`}
                        </p>
                        <p className="text-xs text-gray-500">{caseOpt.caseType}</p>
                      </div>
                      {effectiveCaseId === caseOpt.id && (
                        <svg className="w-5 h-5 text-aalaw-red flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* File Type Filter & Sort */}
          <div className="flex items-center gap-2 flex-1 sm:flex-none">
            <div className="relative flex-1 sm:w-auto">
              <select
                value={filterType}
                onChange={(e) => { setFilterType(e.target.value); setCurrentPage(1); }}
                className="w-full sm:w-auto appearance-none pl-4 pr-10 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-aalaw-red focus:border-transparent cursor-pointer"
              >
                <option value="all">All Types</option>
                {fileTypes.map(type => <option key={type} value={type}>{type}</option>)}
              </select>
              <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
            <div className="relative flex-1 sm:w-auto">
              <select
                value={sortOption}
                onChange={(e) => { setSortOption(e.target.value as SortOption); setCurrentPage(1); }}
                className="w-full sm:w-auto appearance-none pl-4 pr-10 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-aalaw-red focus:border-transparent cursor-pointer"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="name-asc">Name A-Z</option>
                <option value="name-desc">Name Z-A</option>
                <option value="size-desc">Largest</option>
                <option value="size-asc">Smallest</option>
              </select>
              <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Documents List */}
      <div className="flex-1 overflow-y-auto overscroll-contain scrollbar-visible" style={{ WebkitOverflowScrolling: 'touch' }}>
        {!effectiveCaseId ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center px-4">
              <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-gray-700 font-medium mb-1">Select a Case or Inquiry</p>
              <p className="text-gray-500 text-sm">Choose from the dropdown above to view documents</p>
            </div>
          </div>
        ) : paginatedDocs.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center px-4">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <p className="text-gray-700 font-medium mb-1">{filterType !== 'all' ? 'No documents match this filter' : 'No documents yet'}</p>
              <p className="text-gray-500 text-sm mb-4">{filterType !== 'all' ? 'Try selecting a different file type' : 'Upload your first document to get started'}</p>
              {filterType === 'all' && (
                <button onClick={handleUploadClick} className="inline-flex items-center gap-2 px-5 py-2.5 bg-aalaw-red hover:bg-aalaw-red-dark text-white text-sm font-medium rounded-xl transition-colors shadow-sm">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Upload Document
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="p-4 sm:p-6">
            <div className="grid gap-3">
              {paginatedDocs.map(doc => (
                <div key={doc.id} className="flex items-center gap-4 p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors group">
                  <button onClick={() => handlePreview(doc)} className="flex-shrink-0 w-14 h-14 rounded-xl bg-white border border-gray-200 flex items-center justify-center overflow-hidden shadow-sm group-hover:shadow transition-shadow">
                    {doc.fileType?.startsWith('image/') && thumbnailUrls.has(doc.id) ? (
                      <img src={thumbnailUrls.get(doc.id)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-2xl">{getFileIcon(doc.fileType)}</span>
                    )}
                  </button>
                  <button onClick={() => handlePreview(doc)} className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-medium text-gray-900 truncate group-hover:text-aalaw-red transition-colors">{doc.fileName}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="inline-flex items-center px-2 py-0.5 bg-white border border-gray-200 rounded text-xs text-gray-600">{getFileTypeLabel(doc.fileType)}</span>
                      <span className="text-xs text-gray-500">{formatFileSize(doc.fileSize)}</span>
                      <span className="text-xs text-gray-400">•</span>
                      <span className="text-xs text-gray-500">{formatDate(doc.createdAt)}</span>
                    </div>
                  </button>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => handlePreview(doc)} className="p-2.5 text-gray-500 hover:text-aalaw-red hover:bg-white rounded-lg transition-colors" title="Preview">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                    <button onClick={() => handleDownload(doc)} className="p-2.5 bg-aalaw-red hover:bg-aalaw-red-dark text-white rounded-lg transition-colors shadow-sm" title="Download">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex-shrink-0 border-t border-gray-200 px-4 sm:px-6 py-4 flex items-center justify-between bg-gray-50">
          <p className="text-sm text-gray-600">
            Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, filteredAndSortedDocs.length)} of {filteredAndSortedDocs.length}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 active:bg-gray-100 transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-sm font-medium text-gray-700 min-w-[4rem] text-center">{currentPage} / {totalPages}</span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 active:bg-gray-100 transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <FileViewerModal visible={viewerVisible} files={viewerFiles} initialIndex={viewerIndex} onClose={() => setViewerVisible(false)} />
    </div>
  );
}
